import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, ArrowRight } from "lucide-react";
import { fetchEpisodes } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { MaskedLine, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const SOUNDS_GAY_URL =
    "https://customer-assets-7cd3h4nn.emergentagent.net/job_7890e65a-8a22-475d-8f20-42607090552b/artifacts/9b4e96066cc9b252_Gemini_Generated_Image_ojunl3ojunl3ojun.webp";

export default function Voices() {
    const [episodes, setEpisodes] = useState([]);
    const { current, playing, playEpisode } = usePlayer();

    useEffect(() => {
        fetchEpisodes().then(setEpisodes).catch(() => {});
    }, []);

    return (
        <div data-testid="voices-page">
            <section className="bg-paper border-b-2 border-ink">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-16 sm:pt-20 sm:pb-20">
                    <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-pink">
                        The living archive
                    </p>
                    <h1
                        data-testid="voices-heading"
                        className="mt-5 font-display font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-ink max-w-4xl"
                    >
                        <MaskedLine delay={0.1}>Voices on</MaskedLine>
                        <MaskedLine delay={0.25} className="italic text-pink">
                            the record.
                        </MaskedLine>
                    </h1>
                    <Reveal delay={0.4}>
                        <p className="mt-8 max-w-2xl text-base sm:text-lg leading-relaxed text-ink/80">
                            Every voice holds a piece of the story. Meet the people behind
                            the memories, relationships, movements and cultural shifts that
                            have shaped queer life. Each conversation adds another
                            perspective to a history that is still being written.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10">
                    {episodes.map((ep, i) => {
                        const isPlaying = current?.id === ep.id && playing;
                        return (
                            <Reveal key={ep.id} delay={i * 0.08}>
                                <article
                                    data-testid={`voice-card-${ep.id}`}
                                    className={`group bg-cream border-2 border-ink hard-shadow ${
                                        i % 2 ? "rotate-1" : "-rotate-1"
                                    } transition-transform duration-300 hover:rotate-0 hover:-translate-y-1.5`}
                                >
                                    <div className="relative border-b-2 border-ink overflow-hidden">
                                        <img
                                            src={ep.poster_art}
                                            alt={`${ep.guest} on BOMGAY`}
                                            className="w-full aspect-square object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                                        />
                                        <span className="absolute top-4 left-4 bg-pink text-cream font-mono-brand text-xs uppercase tracking-[0.2em] px-3 py-1.5 border-2 border-ink">
                                            EP {ep.number}
                                        </span>
                                    </div>
                                    <div className="p-6 sm:p-7">
                                        <h2 className="font-display font-black text-2xl text-ink">
                                            {ep.guest}
                                        </h2>
                                        <p className="mt-1 font-sub italic text-sm text-ink/70">
                                            {ep.guest_role}
                                        </p>
                                        <p className="mt-2 font-mono-brand text-xs uppercase tracking-[0.15em] text-mustard">
                                            {ep.title}
                                        </p>
                                        <p className="mt-4 text-sm leading-relaxed text-ink/80 border-l-4 border-pink pl-4">
                                            {ep.guest_quote}
                                        </p>
                                        <button
                                            data-testid={`voice-play-${ep.id}`}
                                            onClick={() => playEpisode(ep)}
                                            className="mt-6 inline-flex items-center gap-2 bg-ink text-paper font-sub font-bold text-sm px-5 py-3 border-2 border-ink hard-shadow-sm transition-transform duration-200 hover:-translate-y-0.5"
                                        >
                                            {isPlaying ? (
                                                <Pause className="w-4 h-4" fill="currentColor" />
                                            ) : (
                                                <Play className="w-4 h-4" fill="currentColor" />
                                            )}
                                            {isPlaying ? "Pause episode" : "Hear the episode"}
                                        </button>
                                    </div>
                                </article>
                            </Reveal>
                        );
                    })}

                    <Reveal delay={0.15}>
                        <div className="relative -rotate-1 h-full">
                            <img
                                src={SOUNDS_GAY_URL}
                                alt="'Sounds gay, I'm in' illustration"
                                data-testid="voices-illustration"
                                className="w-full h-full object-cover border-2 border-ink hard-shadow-pink min-h-[320px]"
                            />
                        </div>
                    </Reveal>

                    <Reveal delay={0.2}>
                        <Link
                            to="/"
                            data-testid="voices-cta-card"
                            className="flex flex-col justify-between h-full min-h-[320px] bg-ink text-paper border-2 border-ink hard-shadow p-7 rotate-1 transition-transform duration-300 hover:rotate-0 hover:-translate-y-1"
                        >
                            <div>
                                <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-mustard">
                                    An open mic
                                </p>
                                <p className="mt-4 font-display font-black text-2xl sm:text-3xl leading-snug">
                                    Have a story the archive needs?
                                </p>
                                <p className="mt-4 text-sm text-paper/70 leading-relaxed">
                                    Drop it in the question box on the home page. The next
                                    voice on this wall could be yours.
                                </p>
                            </div>
                            <span className="mt-8 inline-flex items-center gap-2 font-sub font-bold text-pink">
                                Share your story <ArrowRight className="w-4 h-4" />
                            </span>
                        </Link>
                    </Reveal>
                </div>
            </section>

            <Marquee />
        </div>
    );
}
